import { useNavigate } from "react-router";
import { useGestaltung } from "../design/gestaltung";
import { Kachel } from "../design/Kachel";
import { Scrollbereich } from "../design/Scrollbereich";
import { useEingabesperre, useSperre } from "../design/sperre";
import { useZurueck } from "../design/zurueck";
import { OfflineHinweis } from "../plattform/Offline";
import type { RahmenProps, Rahmenart } from "../plattform/rahmen";
import { useJagdzustand, type Zustandsanzeige } from "./jagdzustand";
import { Zustandsband, type Bandart } from "./Zustandsband";

/**
 * Rahmen jeder Ansicht einer Drückjagd außer der Startansicht (oberflaeche.md §3.2, §4.1, F-080; design/README §4.4,
 * §9.2, §9.6, §9.8): Zustandsfläche in der Farbe des Jagdzustands, Zustandsband ganz oben außerhalb von `main`, dann
 * genau ein `main` mit dem Ansichtsnamen. Andere Kontexte bauen ihre Ansichten der Drückjagd damit (über `App.tsx`,
 * `plattform/rahmen.ts`), damit Jagdzustand, Raster und Bedienung überall gleich sind und ohne Neuladen wechseln.
 *
 * Kopfnavigation (V-7): `navigation` „Navigation“ enthält nur `button` „mehr“, das die Ansicht „mehr“ öffnet; Abläufe,
 * Notruf und Dialoge (§5) haben keine. „Zurück“ steht in Ansichten mit Kopf oben links in `main` (optisch in der
 * Kopfzeile des Bands). Hinweise ohne Netz (§9, §10) stehen zuerst in der Liste.
 */
const MAIN: Record<Rahmenart, string> = {
  liste: "feld feld--liste", flaeche: "feld feld--liste", leitung: "feld feld--leitung", eintrag: "feld feld--eintrag", kanal: "feld feld--kanal",
  ablauf: "feld feld--ablauf", notruf: "feld feld--notruf", anruf: "feld feld--anruf", "notruf-ein": "feld feld--notruf-ein", dialog: "feld feld--leitung",
};

const BAND: Record<Rahmenart, Bandart> = {
  liste: "liste", flaeche: "liste", leitung: "liste", eintrag: "liste", kanal: "liste",
  ablauf: "ablauf", notruf: "notruf", anruf: "anruf", "notruf-ein": "start", dialog: "dialog",
};

const MIT_KOPF: ReadonlySet<Rahmenart> = new Set(["liste", "flaeche", "leitung", "eintrag", "kanal"]);

/** Art aus den älteren Angaben `ablauf` (Klassen des Schritts) und `flaeche`. */
function artAus(p: RahmenProps): { art: Rahmenart; klassen: string } {
  if (p.art) return { art: p.art, klassen: p.klassen ?? "" };
  if (typeof p.ablauf === "string") {
    const notruf = ({ "feld--notruf": "notruf", "feld--anruf": "anruf", "feld--notruf-ein": "notruf-ein" } as const)[p.ablauf as "feld--notruf"];
    if (notruf) return { art: notruf, klassen: p.klassen ?? "" };
    return { art: "ablauf", klassen: [p.ablauf, p.klassen].filter(Boolean).join(" ") };
  }
  if (p.ablauf) return { art: "ablauf", klassen: p.klassen ?? "" };
  return { art: p.flaeche ? "flaeche" : "liste", klassen: p.klassen ?? "" };
}

export function AnsichtDerDrueckjagd(p: Omit<RahmenProps, "children"> & { children: RahmenProps["children"] | ((anzeige: Zustandsanzeige) => RahmenProps["children"]) }) {
  const { drueckjagdId, name, children } = p;
  const { art, klassen } = artAus(p as RahmenProps);
  const anzeige = useJagdzustand(drueckjagdId);
  const gesperrt = useSperre(art === "notruf" || art === "anruf" || art === "notruf-ein" || art === "dialog" ? "kritisch" : "ablauf", name);
  const beschaeftigt = useEingabesperre(gesperrt) || undefined;
  const navigate = useNavigate();
  const zurueckStandard = useZurueck(`/drueckjagd/${drueckjagdId}`);
  const wurzel = useGestaltung();
  const inhalt = typeof children === "function" ? children(anzeige) : children;
  const mitKopf = MIT_KOPF.has(art);
  const zurueck = mitKopf && p.zurueck !== false
    ? <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="zurueck-oben" onPress={p.zurueck || zurueckStandard} /> : null;
  const titel = art === "notruf-ein" ? undefined : { titel: p.titel ?? name, unter: p.unter, schritte: p.schritte, symbol: p.titelSymbol };
  const liste = (
    <Scrollbereich abdruck={p.abdruck} kopf={p.listenkopf}>
      <OfflineHinweis ansicht={name} />
      {inhalt}
    </Scrollbereich>
  );
  return (
    <div ref={wurzel} className={`ansicht${gesperrt ? " ist-gesperrt" : ""}`} data-zustand={anzeige.flaeche}>
      <Zustandsband drueckjagdId={drueckjagdId} anzeige={anzeige} art={BAND[art]} titel={titel} status={p.status} kontext={p.kontext} />
      {mitKopf && !p.ohneMehr ? (
        <nav className="kopfnav" aria-label="Navigation">
          <Kachel symbol="mehr" begriff="mehr" art="kompakt" className="mehr-oben" onPress={() => navigate(`/drueckjagd/${drueckjagdId}/mehr`)} />
        </nav>
      ) : null}
      <main className={`${MAIN[art]}${klassen ? ` ${klassen}` : ""}`} aria-label={name} inert={gesperrt || p.sperre} aria-busy={beschaeftigt}>
        {zurueck}
        {art === "liste" || art === "leitung" ? (
          art === "leitung" && p.ueber ? (
            // Bestätigung der Leitung über Zählern, Liste und Auslöser (design/README §9.3); darunter nichts bedienbar
            <><div className="leitung__inhalt" inert>{p.oben}{liste}{p.unten}</div>{p.ueber}</>
          ) : (
            <>{p.oben}{liste}{p.unten}{p.ueber}</>
          )
        ) : art === "flaeche" ? (
          <>
            {p.oben}
            <OfflineHinweis ansicht={name} />
            {inhalt}
            {p.unten}
            {p.ueber}
          </>
        ) : (
          <>
            {p.oben}
            {inhalt}
            {p.unten}
            {p.ueber}
          </>
        )}
      </main>
    </div>
  );
}
